import { supabase } from './supabase'

// Optional: Resend template IDs (set in Supabase Edge Function secrets)
// If not set, will fall back to inline templates
const RESEND_TEMPLATE_IDS = {
  invitation: import.meta.env.VITE_RESEND_TEMPLATE_INVITATION,
  welcome: import.meta.env.VITE_RESEND_TEMPLATE_WELCOME,
  reminder: import.meta.env.VITE_RESEND_TEMPLATE_REMINDER,
  signup_token: import.meta.env.VITE_RESEND_TEMPLATE_SIGNUP_TOKEN,
}

/**
 * Send email via Supabase Edge Function
 * Supports both Resend templates (via template_id) and inline templates
 */
const sendEmail = async (emailData) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session) {
      throw new Error('User must be authenticated to send emails')
    }

    const { data, error } = await supabase.functions.invoke('resend-email', {
      body: emailData,
    })

    if (error) {
      console.error('Supabase Edge Function error:', error)
      // Provide more helpful error messages
      if (error.message?.includes('Function not found') || error.message?.includes('404')) {
        throw new Error('Email service not configured. Please contact support.')
      }
      if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
        throw new Error('Authentication failed. Please try logging in again.')
      }
      throw error
    }

    // Check if the response contains an error
    if (data?.error) {
      console.error('Email service error:', data)
      const errorMessage = data.details?.message || data.error || 'Failed to send email'
      throw new Error(errorMessage)
    }

    return data
  } catch (error) {
    console.error('Error sending email:', error)
    // Re-throw with more context if it's a network/function error
    if (error.message && !error.message.includes('Email service') && !error.message.includes('Authentication')) {
      throw new Error(`Email service error: ${error.message}`)
    }
    throw error
  }
}

/**
 * Send organization invitation email
 */
export const sendInvitationEmail = async (email, invitationData) => {
  const inviteLink = `${window.location.origin}/onboarding?invite=${invitationData.token}`

  // Use Resend template if available, otherwise use inline template
  const emailPayload = {
    to: email,
    subject: `You've been invited to join ${invitationData.organizationName}`,
    data: {
      organizationName: invitationData.organizationName,
      role: invitationData.role,
      inviteLink,
    },
  }

  // Add organizationId if provided (for dynamic "from" email)
  if (invitationData.organizationId) {
    emailPayload.organizationId = invitationData.organizationId
  }

  // Prefer Resend template if template ID is configured
  if (RESEND_TEMPLATE_IDS.invitation) {
    emailPayload.resend_template_id = RESEND_TEMPLATE_IDS.invitation
  } else {
    emailPayload.template = 'invitation'
  }

  return sendEmail(emailPayload)
}

/**
 * Send welcome email
 */
export const sendWelcomeEmail = async (email, welcomeData) => {
  const emailPayload = {
    to: email,
    subject: `Welcome to ${welcomeData.organizationName}!`,
    data: {
      userName: welcomeData.userName,
      organizationName: welcomeData.organizationName,
      dashboardUrl: `${window.location.origin}/dashboard`,
    },
  }

  // Add organizationId if provided (for dynamic "from" email)
  if (welcomeData.organizationId) {
    emailPayload.organizationId = welcomeData.organizationId
  }

  if (RESEND_TEMPLATE_IDS.welcome) {
    emailPayload.resend_template_id = RESEND_TEMPLATE_IDS.welcome
  } else {
    emailPayload.template = 'welcome'
  }

  return sendEmail(emailPayload)
}

/**
 * Send follow-up reminder email
 */
export const sendReminderEmail = async (email, reminderData) => {
  const emailPayload = {
    to: email,
    subject: `Reminder: ${reminderData.title}`,
    data: {
      title: reminderData.title,
      description: reminderData.description,
      clientName: reminderData.clientName,
      clientId: reminderData.clientId,
      reminderDate: reminderData.reminderDate,
      clientUrl: `${window.location.origin}/clients/${reminderData.clientId}`,
    },
  }

  // Add organizationId if provided (for dynamic "from" email)
  if (reminderData.organizationId) {
    emailPayload.organizationId = reminderData.organizationId
  }

  if (RESEND_TEMPLATE_IDS.reminder) {
    emailPayload.resend_template_id = RESEND_TEMPLATE_IDS.reminder
  } else {
    emailPayload.template = 'reminder'
  }

  return sendEmail(emailPayload)
}

/**
 * Send signup token email
 */
export const sendSignupTokenEmail = async (email, signupData) => {
  const signupLink = `${window.location.origin}/onboarding?token=${signupData.token}`

  const emailPayload = {
    to: email,
    subject: `Set up ${signupData.organizationName}`,
    data: {
      adminName: signupData.adminName,
      organizationName: signupData.organizationName,
      signupLink,
    },
  }

  // Add organizationId if provided (for dynamic "from" email)
  // Note: For signup tokens, organization doesn't exist yet, so this is optional
  if (signupData.organizationId) {
    emailPayload.organizationId = signupData.organizationId
  }

  if (RESEND_TEMPLATE_IDS.signup_token) {
    emailPayload.resend_template_id = RESEND_TEMPLATE_IDS.signup_token
  } else {
    emailPayload.template = 'signup_token'
  }

  return sendEmail(emailPayload)
}

export default {
  sendInvitationEmail,
  sendWelcomeEmail,
  sendReminderEmail,
  sendSignupTokenEmail,
}

